import { AccountTreeRounded, ArrowBackRounded, CloseRounded, ConstructionRounded, DoneRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import { changeRoute } from '../../store/slices/SessionSlice'
import useCommand, { CommandStatus } from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'

interface P {
    setOpenModal: (openModal: boolean) => void
}

export default function CreateNewFlowModal({ setOpenModal }: P) {
    const [page, setPage] = useState(0)
    const [templateName, setTemplateName] = useState('')
    const dispatch = useDispatch()
    const invalidateApprovedTemplates = useInvalidate({ queryKey: buildQueryKey('approvedTemplates') })
    const invalidatePendingTemplates = useInvalidate({ queryKey: buildQueryKey('notApprovedTemplates') })

    const { status, execute } = useCommand({
        commandFn: async (options: { name: string, fromFlowTemplateUUID?: string }) => await ApiClient.Flow.createFlowTemplate(options.name, options.fromFlowTemplateUUID),
        onFinish: copiedFlow => {
            if (copiedFlow.status === 'success') {
                invalidateApprovedTemplates()
                invalidatePendingTemplates()
                dispatch(changeRoute({ route: 'flow', params: { flowId: copiedFlow.response.uuid } }))
            }
            if (copiedFlow.status === 'error') {
                snackbar.show({ severity: 'error', text: copiedFlow.errorText })
            }
            setPage(1)
        }
    })

    const createTemplate = useCallback((name: string, fromFlowTemplateUUID?: string) => {
        execute({ name, fromFlowTemplateUUID })
    }, [execute])

    return (
        <Paper sx={{ display: 'flex', flexDirection: 'column', padding: 2, width: 650, height: page === 0 ? 400 : 600 }}>
            {page === 0 && (
                <FromScratch setPage={setPage} setOpenModal={setOpenModal} createTemplate={createTemplate} status={status} templateName={templateName} setTemplateName={setTemplateName} />
            )}
            {page === 1 && (
                <CopyFrom setPage={setPage} setOpenModal={setOpenModal} createTemplate={createTemplate} status={status} templateName={templateName} />
            )}
        </Paper>
    )
}

interface FromScratchP {
    setPage: (page: number) => void
    setOpenModal: (openModal: boolean) => void
    createTemplate: (name: string, fromFlowTemplateUUID?: string) => void
    status: CommandStatus
    templateName: string
    setTemplateName: (templateName: string) => void

}

const FromScratch = ({ setPage, setOpenModal, createTemplate, status, templateName, setTemplateName }: FromScratchP) => {
    return (
        <Stack spacing={3}>
            <Stack flexDirection='row' justifyContent='space-between'>
                <Typography variant='h6'>Create a new template</Typography>
                <IconButton
                    size='small'
                    onClick={() => {
                        setOpenModal(false)
                        setPage(0)
                    }}>
                    <CloseRounded fontSize='small' />
                </IconButton>
            </Stack>
            <TextField size='small' fullWidth value={templateName} onChange={e => setTemplateName(e.target.value)} label='New template name' placeholder='e.g. Custom MAPPO Template' />
            <Stack flexDirection='row' gap={3}>
                <Stack alignItems='center' width='50%' spacing={2}>
                    <ConstructionRounded fontSize='large' color='primary' />
                    <Typography variant='subtitle1'>Build from scratch</Typography>
                    <Typography variant='caption' textAlign='center'>Use the drag-and-drop editor to design your settlement engine from the ground up. Add nodes, connect them, and shape the logic exactly how you want it.</Typography>
                    <LoadingButton
                        loading={status === 'loading'}
                        onClick={() => createTemplate(templateName)}
                        variant='contained'>Start Fresh
                    </LoadingButton>
                </Stack>
                <Stack alignItems='center' width='50%' spacing={2}>
                    <AccountTreeRounded fontSize='large' color='secondary' />
                    <Typography variant='subtitle1'>Clone a template</Typography>
                    <Typography variant='caption' textAlign='center'>Save time by duplicating an existing template. All nodes and connections are copied over, and you can still fine-tune the cloned engine to make it unique for this arrangement.</Typography>
                    <Button
                        onClick={() => {
                            setPage(1)
                        }}
                        variant='contained'
                        color='secondary'>Start From a Clone
                    </Button>
                </Stack>
            </Stack>
        </Stack>
    )
}

interface CopyFromP {
    setPage: (page: number) => void
    setOpenModal: (openModal: boolean) => void
    createTemplate: (name: string, fromFlowTemplateUUID?: string) => void
    status: CommandStatus
    templateName: string
}

const slotProps: any = {
    loadingOverlay: {
        variant: 'skeleton',
        noRowsVariant: 'skeleton'
    }
}
const sx = {
    '&.MuiDataGrid-root': {
        padding: 3,
        borderRadius: '12px'
    }, '& .MuiDataGrid-cell:focus': {
        outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    }
}

const columns: GridColDef<any>[] = [
    { field: 'name', headerName: 'Name', width: 300 },
    { field: 'version', headerName: 'Version', valueGetter: (_, row) => row.versioning.version },
    { field: 'createdAt', headerName: 'Created at', valueGetter: (_, row) => new Date(row.createdAt).toLocaleDateString() },
    { field: 'nodes', headerName: '# of nodes', valueGetter: (_, row) => row.nodes.length }
]

const CopyFrom = ({ setPage, setOpenModal, createTemplate, status, templateName }: CopyFromP) => {
    const [selectedFlowTemplateUUID, setSelectedFlowTemplateUUID] = useState<string | null>(null)

    const { status: copyFromStatus, response: copyFromResponse } = useQuery({
        queryKey: buildQueryKey('getListCopyFromFlow'),
        queryFn: () => ApiClient.Flow.getListCopyFromFlowTemplate(),
        cache: false
    })

    return (
        <Stack spacing={3} overflow='hidden' flex={1}>
            <Stack flexDirection='row' justifyContent='space-between'>
                <Typography variant='h6'>Select an existing settlement template to clone</Typography>
                <IconButton
                    size='small'
                    onClick={() => {
                        setOpenModal(false)
                        setPage(1)
                        setSelectedFlowTemplateUUID(null)
                    }}>
                    <CloseRounded fontSize='small' />
                </IconButton>
            </Stack>
            <DataGrid
                sx={sx}
                slotProps={slotProps}
                loading={copyFromStatus === 'loading' || copyFromStatus === 'stale'}
                getRowId={row => row.uuid}
                columns={columns}
                rows={copyFromResponse}
                hideFooter
                disableMultipleRowSelection
                rowSelectionModel={selectedFlowTemplateUUID ? [selectedFlowTemplateUUID] : []}
                onRowSelectionModelChange={newSelection => {
                    const uuid = newSelection[0]?.toString() ?? null
                    setSelectedFlowTemplateUUID(uuid)
                }}
            />

            <Stack flexDirection='row' alignSelf='flex-end' gap={1}>
                <Button
                    disabled={status === 'loading' || copyFromStatus === 'loading' || copyFromStatus === 'stale'}
                    onClick={() => {
                        setPage(0)
                        setSelectedFlowTemplateUUID(null)
                    }}
                    size='small'
                    variant='outlined'
                    startIcon={<ArrowBackRounded fontSize='small' />}>go back
                </Button>

                <LoadingButton
                    loading={status === 'loading'}
                    disabled={!selectedFlowTemplateUUID}
                    loadingPosition='start'
                    onClick={() => createTemplate(templateName, selectedFlowTemplateUUID)}
                    size='small'
                    variant='contained'
                    startIcon={<DoneRounded fontSize='small' />}>confirm selection
                </LoadingButton>
            </Stack>
        </Stack>
    )
}
